import {AbstractLitElement} from "../abstract-lit.element";
import {customElement} from "lit/decorators/custom-element.js";
import {html, nothing, TemplateResult} from "lit";
import {Entity} from "../../domain/entities/entity";
import {PropertyValues} from "@lit/reactive-element";
import {property} from "lit/decorators/property.js";
import {container} from "tsyringe";
import {AuditLogRepository} from "../../domain/repositories/audit-log.repository";
import {translate} from "lit-translate";
import {state} from "lit/decorators/state.js";
import {classMap} from "lit/directives/class-map.js";
import {AuditLog} from "../../domain/entities/audit-log.entity";
import {DateHelper} from "../../helpers/date.helper";
import {until} from "lit/directives/until.js";
import {EntityManager} from "../../persistence/entity.manager";
import {PROPERTY_INFO, VIEW_COMPONENT} from "../../symbols";
import {AbstractViewerComponent} from "./abstract-viewer.component";
import {PropertyInfo} from "../../types/property-info.type";
import {virtualize} from "@lit-labs/virtualizer/virtualize.js";
import {OrderParameter} from "../../connectivity/order-parameter.interface";
import {AuditLogDTO} from "../../domain/dtos/audit-log.dto";
import {groupAuditLogs, AuditLogGroup} from "../../services/audit-log-grouping";
import {deriveBusinessEventKey} from "../../services/audit-business-event.mapping";

@customElement(`ha-audit-log-viewer`)
export class HaAuditLogViewerComponent<TEntity extends Entity> extends AbstractLitElement {

    @property({type: Object})
    public entities: TEntity[] = [];

    /**
     * TR1 — optional/additive props (O2D-PRD-103). Backward-compatible with the
     * existing `.entities` mounts (`ha-form-issue.component.ts`,
     * `issue-dialog-renderer.service.ts`), which pass only `.entities`. These are
     * consumed by the grouping/render restructure (T3.2) and the repository
     * `sorting` param (T4.1); declaring them here does not change current behaviour.
     */
    @property({type: String})
    public entityUid?: string;

    @property({type: Object})
    public mainEntity?: TEntity;

    /** Optional sort spec; defaults to `createdAt desc` in the repository (TR2). */
    @property({type: Object})
    public sorting?: OrderParameter<AuditLogDTO>[];

    @state()
    private _isOpen = false;

    private _repository = container.resolve<AuditLogRepository>(AuditLogRepository);

    /**
     * The loaded rows grouped into business-event / technical groups (TR4). `null`
     * until the accordion is first opened and the rows are loaded + grouped.
     */
    @state()
    private _groups: AuditLogGroup<TEntity>[] | null = null;

    /**
     * TR4/DD1 — per-group expand state, held SEPARATE from the whole-viewer
     * `_isOpen` accordion (a single shared flag cannot serve both). Keyed by the
     * stable group id ({@link _groupId}) so the expand state cannot misapply to a
     * recycled virtualizer item — the same id drives `virtualize`'s `keyFunction`.
     */
    @state()
    private _expandedGroups: Set<string> = new Set<string>();

    /** Guards against re-triggering the load while one is already in flight. */
    private _isLoading = false;

    protected willUpdate(changedProperties: PropertyValues): void {

        if ((changedProperties.has(`_isOpen`) || changedProperties.has(`entities`))
            && this._isOpen
            && this._groups == null
            && !this._isLoading
            && this.entities.length > 0
        ) {
            this._isLoading = true;
            const mainEntityUid = this.entities[0]!.uid;
            this._repository.findAllByEntities(this.entities)
                .then(auditLogs => auditLogs.filter(a => ![a.newValue, a.oldValue].includes(mainEntityUid)))
                .then(auditLogs => {
                    // TR4 — group the filtered rows via the pure grouping module
                    // BEFORE handing them to the virtualizer (no grouping inline).
                    this._groups = groupAuditLogs(auditLogs);
                })
                .finally(() => {
                    this._isLoading = false;
                });
        }
    }

    protected render(): unknown {
        const cssClasses = {
            "is-open": this._isOpen,
            "is-closed": !this._isOpen,
            "audit-log__body": true
        }

        return html`
            <label @click=${this._toggleOpenState}>
                ${translate(`audit.log.title`)}
            </label>
            <div class=${classMap(cssClasses)}>
                ${this._groups == null
                    ? html`<div>&nbsp;</div>`
                    : this._renderGroups(this._groups)}
            </div>
        `;
    }

    private _toggleOpenState(): void {
        this._isOpen = !this._isOpen;
    }

    /**
     * TR4 — render the loaded groups through the virtualizer. `keyFunction` gives
     * each group a STABLE identity (the group id), so the virtualizer keeps the
     * item ↔ group binding across DOM recycling on scroll; without it a recycled
     * item would show the wrong group's expand state (plan-review P1.1).
     *
     * The per-item group render lives in the dedicated {@link _renderGroup} method
     * (extracted in T3.3), kept distinct from {@link _renderAuditLog} (the detail
     * row render, reused 1:1 and left untouched).
     *
     * Scroll-anchoring: `@lit-labs/virtualizer` does NOT anchor on item resize
     * (`reference_lit-virtualizer-scroll-anchoring.md`). The detail region reserves
     * height / is contained via the `audit-log__group*` classes (styled in
     * `auditlog.css`, T5.2) so expanding a group does not resize the virtualized item
     * and cause a scroll-jump.
     */
    private _renderGroups(groups: AuditLogGroup<TEntity>[]): unknown {
        return virtualize({
            items: groups,
            scroller: true,
            keyFunction: (group: AuditLogGroup<TEntity>) => this._groupId(group),
            renderItem: (group: AuditLogGroup<TEntity>) => this._renderGroup(group)
        });
    }

    /**
     * Render a single virtualized group item (T3.3): a technical group renders its
     * raw rows LOUDLY marked and never hidden (G3); a business-event group renders a
     * derived-label header plus a per-group expandable detail region. Distinct from
     * {@link _renderAuditLog}, which renders one detail row and is reused here 1:1.
     */
    private _renderGroup(group: AuditLogGroup<TEntity>): TemplateResult {
        const groupId = this._groupId(group);
        const canExpand = group.detailRows.length > 0;
        const isExpanded = this._expandedGroups.has(groupId);

        // Technical group (command-less row, or a commandUid group with no intent
        // row) → render raw + LOUDLY marked, never hidden (G3).
        if (group.isTechnical) {
            return html`
                <div class="audit-log__group audit-log__group--technical">
                    <div class="audit-log__group__technical-marker label">
                        ${translate(`audit.log.technicalEntry`)}
                    </div>
                    ${group.detailRows.map(row => this._renderAuditLog(row))}
                </div>
            `;
        }

        // Business-event group → derived-label header + per-group expand.
        const headerRow = group.headerRow!;
        const eventKey = deriveBusinessEventKey(
            headerRow.commandName ?? null,
            group.detailRows.map(row => row.property)
        );
        const label = eventKey.kind === `key`
            ? translate(eventKey.key)
            : translate(`audit.log.technicalEntry`);

        return html`
            <div class="audit-log__group">
                <div class="audit-log__group__header"
                     ?data-expandable=${canExpand}
                     ?data-expanded=${isExpanded}
                     @click=${canExpand ? () => this._toggleGroup(groupId) : undefined}>
                    <div class="audit-log__group__label">${label}</div>
                    <div class="audit-log__group__meta">
                        <div class="audit-log__entry__text">
                            ${DateHelper.fromDateTimeToDateTimeString(headerRow.createdAt)}
                        </div>
                        <div class="label">${translate(`audit.log.item.user`)}</div>
                        <div class="audit-log__entry__text">
                            ${until(headerRow.user?.then(user => !!user ? user.toString() : `unknown`), html`
                                <span>Lade...</span>`)}
                        </div>
                    </div>
                </div>
                <div class="audit-log__group__detail" ?data-expanded=${isExpanded}>
                    ${isExpanded
                        ? group.detailRows.map(row => this._renderAuditLog(row))
                        : nothing}
                </div>
            </div>
        `;
    }

    /**
     * Stable identity for a group — drives both the virtualizer `keyFunction` and
     * the `_expandedGroups` map. Business-event and no-intent commandUid groups use
     * their (unique) `commandUid`; a command-less technical group (always exactly
     * one row) uses that row's uid.
     */
    private _groupId(group: AuditLogGroup<TEntity>): string {
        return group.commandUid ?? group.detailRows[0]!.uid;
    }

    /**
     * Toggle a single group's expand state. Synchronous (no async work) so no
     * in-flight guard is needed; a fresh Set is assigned so the `@state` change is
     * detected — mutating the existing Set in place would not trigger a re-render.
     */
    private _toggleGroup(groupId: string): void {
        const next = new Set(this._expandedGroups);
        if (next.has(groupId)) {
            next.delete(groupId);
        } else {
            next.add(groupId);
        }
        this._expandedGroups = next;
    }

    private _renderAuditLog(auditLog: AuditLog<TEntity>): TemplateResult {
        return html`
            <div class="audit-log__entry">
                <div class="audit-log__entry-row1">
                    <div class="audit-log__entry__text">${DateHelper.fromDateTimeToDateTimeString(auditLog.createdAt)}
                    </div>
                    <div class="label">${translate(`audit.log.item.user`)}</div>
                    <div class="audit-log__entry__text">
                        ${until(auditLog.user?.then(user => !!user ? user.toString() : `unknown`), html`
                            <span>Lade...</span>`)}
                    </div>
                </div>
                <div class="audit-log__entry-entity">
                    <div class="label">${translate(`audit.log.item.entityName`)}</div>
                    <div>${translate(`audit.log.item.entity.${auditLog.entityClass}.name`)}</div>
                </div>
                <div class="audit-log__entry-row2">
                    <div class="audit-log__entry__text">
                        ${translate(`audit.log.item.entity.${auditLog.entityClass}.${auditLog.property}`)}
                    </div>
                    <div class="label">${translate(`audit.log.item.old`)}</div>
                    <div class="audit-log__entry__text">
                        ${this._renderAuditLogValue(auditLog.entityClass, auditLog.property, auditLog.oldValue)}
                    </div>
                    <div class="label">${translate(`audit.log.item.new`)}</div>
                    <div class="audit-log__entry__text">
                        ${this._renderAuditLogValue(auditLog.entityClass, auditLog.property, auditLog.newValue)}
                    </div>
                </div>
            </div>
        `
    }

    private _renderAuditLogValue(entityClass: string, propertyKey: string, value: any): HTMLElement {
        const repository = container.resolve(EntityManager).getRepositoryForEntityByName(entityClass);
        if (!repository) {
            throw new Error(`Repository for entity ${entityClass} not found`);
        }
        const propertyMetadata: {
            propertyType: any,
            viewComponentName: string,
            settings: Record<string, any> | null
        } = Reflect.getMetadata(VIEW_COMPONENT, repository!.entity, propertyKey) ?? 'ha-string-viewer';

        const propertyInfo: PropertyInfo | null = Reflect.getMetadata(PROPERTY_INFO, repository!.entity, propertyKey) ?? null
        if (!propertyInfo) {
            throw new Error(`Property info for property ${entityClass}.${propertyKey} not found`);
        }

        const element = document.createElement(propertyMetadata.viewComponentName) as AbstractViewerComponent<any>;
        element.value = value;
        element.propertyInfo = propertyInfo;
        element.settings = propertyMetadata.settings;

        return element;
    }

}